/* eslint-disable react-hooks/exhaustive-deps */
import './index.less';

import {
  Box,
  Button,
  Flex,
  Grid,
  Icon,
  Input,
  Space,
  Spinner,
  Switch,
  Text,
  Textarea,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { ClockIcon } from '@tonic-one/react-icons';
import { useToggle } from '@tonic-one/react-hooks';
import dayjs from 'dayjs';
import i18n from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import common, { handleNavigate } from 'src/utils/common';
import { ProvisionApi, ReportApi } from '@Apis';
import MenuWithLabel from '../../../../components/Common/Menu';
import { PermissionNameEnum } from '@Types/permissions';
import config from '../../../../config';
import { useLoginUserStore } from '@Models/useLoginUserModel';
import { usePermissionStore } from '@Models/usePermissionModel';
import { useReportTabsStore } from '@Models/useReportTabsModel';
import { useSiderWidthStore } from '@Models/useSiderWidthModel';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import { usePrimaryBoxStyle } from '../../../../style';
import { ServiceNameEnum } from '../../../../types';
import {
  FormatContent,
  ReportContentItems,
  ReportContentItemsKey,
} from '../../../../types/report';
import { fillZeroToString, validateEmail } from '../../../../utils/common';
import {
  Frequency,
  MONTH_DAY,
  SCHEDULE_FREQUENCY,
  SCHEDULE_FREQUENCY_TIP,
  TEMPLATE_LIST,
  WEEK_DAY,
} from '../../../../utils/enums';
import {
  SERVICE_TYPES,
  getServiceNamesMapping,
} from '../../../../utils/service';
import { reportPendo } from '../../../../utils/setupPendo';
import BlockTitle from '../../BlockTitle';
import FormatContainer from '../../FormatContainer';
import LoadingBtn from '../../LoadingBtn';
import { MemoTextLabel, MemoTextLabelWithInfo } from '../../MemoTextLabel';
import NavBack from 'src/components/Common/NavBack';
import ReportContentCheckbox from '../../ReportContentCheckbox';
import ReportPreviewModal from '../../ReportPreviewModal';
import SelectTag from '../../SelectTag';
import ServiceCheckbox from '../../ServiceCheckbox';
import TimezoneTip from '../../TimezoneTip';
import ViewPDFSampleButton from '../../ViewPDFSampleButton';
import { Info } from '@Components/Common/Info';
import { useOrgStore } from '@Models/useOrgModel';

export default function AddSchedule() {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const primaryBoxStyle = usePrimaryBoxStyle();
  const loginUserModel = useLoginUserStore();
  const { siderWidth } = useSiderWidthStore();
  const { offset } = useUtcOffsetStore();
  const orgStore = useOrgStore();
  const { setTabStatus } = useReportTabsStore();
  const permissionModel = usePermissionStore();
  //default value
  const statusDefault = true;
  const frequencyDefault = Frequency.Daily;
  const descriptionDefault = '';
  const templateDefault = 'overall threat detections';
  const reportContentDefault = config.ui.reportDefaultValue.reportContent;
  const recipientFormDefault = {
    subject: t('reportCommon.emailSubject'),
    message: '',
  };
  //state
  const [scheduleId, setScheduleId] = useState('');
  const [status, setStatus] = useState(statusDefault);
  const [reportName, setReportName] = useState('');
  const [frequency, setFrequency] = useState(frequencyDefault);
  const [time, setTime] = useState(dayjs().utcOffset(offset).format('HH:mm'));
  const [timeFormatError, setTimeFormatError] = useState(false);
  const [timezoneOffset, setTimezoneOffset] = useState(offset);
  const [dayOfWeek, setDayOfWeek] = useState('1');
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [description, setDescription] = useState(descriptionDefault);
  const [orgId, setOrgId] = useState(orgStore.orgId);
  const [checkedServices, setCheckedServices] = useState<ServiceNameEnum[]>([]);
  // const [provisionedServices, setProvisionedServices] = useState<string[]>([]);
  const [template, setTemplate] = useState(templateDefault);
  const [reportContent, setReportContent] =
    useState<ReportContentItems>(reportContentDefault);
  const formatType = 'pdf';
  const [accountOpts, setAccountOpts] = useState<
    { account: string; email: string }[]
  >([]);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [recipientForm, setRecipientForm] = useState(recipientFormDefault);
  const [recipientError, setRecipientError] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [isPreviewOpen, previewOpenToggle] = useToggle(false); //control preview open
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const navigate = useNavigate();
  const { state } = useLocation();
  const [formatContent, setFormatContent] = useState<FormatContent>(
    config.ui.reportDefaultValue.formatContent
  );

  const goBack = function () {
    setTabStatus({ tabIndex: 1, submitSuccess: undefined });
    const path = '/report';
    handleNavigate(path);
    navigate(path, { replace: true });
  };

  function handleEmailChange(value: any) {
    setRecipients(value);
  }
  //init
  useEffect(() => {
    const scheduleId = state?.id;
    // edit
    if (scheduleId) {
      setScheduleId(scheduleId);
      getDetail(scheduleId);
    } else {
      setPageLoading(false);
      queryDefault();
      fetchAndSetProvisionedServices(orgId);
    }
  }, []);

  //time format error
  const timeValidate = (val: string) => {
    if (!val) return;
    //24 hour reg
    const regExp = new RegExp(/^([0-1][0-9]|2[0-3]):([0-5][0-9])$/);
    if (!regExp.test(val)) {
      setTimeFormatError(true);
    } else {
      setTimeFormatError(false);
    }
  };

  const resetReportData = function () {
    if (scheduleId) {
      getDetail(scheduleId);
    } else {
      setStatus(statusDefault);
      setReportName(
        (loginUserModel.user?.accountName || '') +
          t('reportCommon.placeholders.defaultScheduleName')
      );
      setFrequency(frequencyDefault);
      setTime(dayjs().utcOffset(offset).format('HH:mm'));
      setDescription(descriptionDefault);
      setOrgId(orgStore.orgId);
      fetchAndSetProvisionedServices(orgStore.orgId);
      setTemplate(templateDefault);
      setReportContent(config.ui.reportDefaultValue.reportContent);
      setRecipients([]);
      setRecipientForm(recipientFormDefault);
      setFormatContent({
        ...formatContent,
        coverDescription: '',
      });
      queryDefault();
    }
    setTimeFormatError(false);
    setRecipientError(false);
  };

  const getDetail = async function (scheduleId: string) {
    const res = await ReportApi.getScheduleDetail(scheduleId);
    setPageLoading(false);
    if (res?.data) {
      const data: any = res.data;
      setStatus(data.enabledStatus === 1);
      setReportName(data.name);
      setDescription(data.description);
      setFrequency(data.frequency);
      setDayOfWeek(data?.frequencyContent.dayOfWeek + '' || '1');
      setDayOfMonth(data?.frequencyContent.dayOfMonth + '' || '1');
      setTime(
        fillZeroToString(data?.frequencyContent.hour) +
          ':' +
          fillZeroToString(data?.frequencyContent.minute)
      );
      setTimeFormatError(false);
      setTimezoneOffset(data.timezoneOffset);
      setTemplate(data.templateName);
      setCheckedServices(data.servicesTypes as ServiceNameEnum[]);
      setOrgId(data.isGroup ? 'all' : data.companyGuid);
      if (Array.isArray(data.content)) {
        const temp_content = {
          total: false,
          bec: false,
          phishing: false,
          ransomware: false,
          maliciousFile: false,
          maliciousURL: false,
          spam: false,
          anomaly: false,
          spoofing: false,
          dlp: false,
        };
        data.content.forEach((item: string) => {
          temp_content[item as ReportContentItemsKey] = true;
        });
        setReportContent(temp_content);
      }
      if (data.format === 'pdf' && data?.formatContent) {
        const { coverDescription, companyName, displayTrendLogo, companyLogo } =
          data.formatContent;
        setFormatContent({
          coverDescription,
          companyName,
          isCompanyLogoDisplay: companyLogo.displayLogo,
          isTrendLogoDisplay: displayTrendLogo,
          fileInfo: {
            fileError: '',
            fileName: companyLogo.fileName,
            fileType: companyLogo.fileType,
            fileSize: companyLogo.fileSize,
            storageId: companyLogo.storageId,
          },
          isDefaultSettingSave: false,
        });

        setRecipients(data?.emailDeliveryInfo.recipients);
        setRecipientForm({
          subject: data?.emailDeliveryInfo.subject,
          message: data?.emailDeliveryInfo.message,
        });
      } else {
        queryDefault();
      }
    }
  };

  const queryDefault = async () => {
    const defaultRes = await ReportApi.getDefaultSetting();
    if (defaultRes.data?.companyLogo) {
      const { companyName, displayTrendLogo, companyLogo } = defaultRes.data;
      setFormatContent({
        coverDescription: '',
        companyName,
        isTrendLogoDisplay: displayTrendLogo === 1,
        isCompanyLogoDisplay: companyLogo.displayLogo,
        fileInfo: {
          fileError: '',
          fileName: companyLogo.fileName,
          fileType: companyLogo.fileType,
          fileSize: companyLogo.fileSize,
          storageId: companyLogo.storageId,
        },
        isDefaultSettingSave: false,
      });
    } else {
      setFormatContent({
        coverDescription: '',
        companyName: '',
        isTrendLogoDisplay: true,
        isCompanyLogoDisplay: true,
        fileInfo: {
          fileError: '',
        },
        isDefaultSettingSave: false,
      });
    }
  };

  const submitData = async function () {
    //validate
    setReportName(reportName.trim());
    if (reportName.trim() === '') {
      scrollToError('scheduleName');
      return;
    }
    if (timeFormatError) {
      scrollToError('frequencyDayTime');
      return;
    }
    if (formatContent.fileInfo?.fileError) {
      scrollToError('uploadLogoButton');
      return;
    }
    if (recipientError) {
      scrollToError('recipient-select');
      return;
    }
    if (
      formatType === 'pdf' &&
      formatContent.isCompanyLogoDisplay &&
      !formatContent.fileInfo.fileName
    ) {
      setFormatContent({
        ...formatContent,
        fileInfo: {
          ...formatContent.fileInfo,
          fileError: 'required',
        },
      });
      scrollToError('uploadLogoButton');
      return;
    }
    //start submit
    setLoading(true);
    setDisabled(true);

    const {
      coverDescription,
      companyName,
      isCompanyLogoDisplay,
      fileInfo,
      isTrendLogoDisplay,
      isDefaultSettingSave,
    } = formatContent;

    let storageId = fileInfo?.storageId;
    if (!storageId && fileInfo.fileData) {
      const saveLogoRes = await ReportApi.saveLogo(fileInfo.fileData);
      if (saveLogoRes.data?.storageId) {
        storageId = saveLogoRes.data?.storageId;
      } else {
        if (scheduleId) {
          window.showNotification({
            titleMsg: t('reportCommon.alert.updateSchedule.fail'),
            bodyMsg: t('reportCommon.alert.updateSchedule.failDesc'),
            severity: 'error',
          });
        } else {
          window.showNotification({
            titleMsg: t('reportCommon.alert.createSchedule.fail'),
            bodyMsg: t('reportCommon.alert.createSchedule.failDesc'),
            severity: 'error',
          });
        }
        return;
      }
    }

    const formatSetting = {
      companyName,
      companyLogo: {
        displayLogo: isCompanyLogoDisplay,
        fileName: fileInfo?.fileName,
        fileType: fileInfo?.fileType,
        fileSize: fileInfo?.fileSize,
        storageId,
      },
      displayTrendLogo: isTrendLogoDisplay,
    };
    if (isDefaultSettingSave) {
      await ReportApi.saveDefaultSetting(formatSetting);
    }
    const params = {
      enabledStatus: status,
      name: reportName.trim(),
      description,
      frequency,
      frequencyContent: {
        dayOfMonth: parseInt(dayOfMonth),
        dayOfWeek: parseInt(dayOfWeek),
        hour: parseInt(time.split(':')[0]),
        minute: parseInt(time.split(':')[1]),
      },
      templateName: template,
      servicesTypes: checkedServices,
      content: (Object.keys(reportContent) as ReportContentItemsKey[]).filter(
        (key) => {
          return reportContent[key];
        }
      ),
      format: formatType,
      formatContent: {
        coverDescription,
        ...formatSetting,
      },
      emailDeliveryInfo: {
        recipients,
        ...recipientForm,
      },
      companyGuid: orgId,
      timezoneOffset: offset,
    };
    let res: any;
    try {
      if (scheduleId) {
        res = await ReportApi.updateSchedule(scheduleId, params);
      } else {
        res = await ReportApi.createSchedule({
          ...params,
          language: i18n.language,
        });
      }
      if (res.data?.id || res.data?.affected === 1) {
        // go to schedule report tab and refresh when success
        setTabStatus({ tabIndex: 1, submitSuccess: true });
        navigate('/report', {
          state: {
            scheduleSuccess: scheduleId ? 'update' : 'create',
          },
          replace: true,
        });
      }
    } catch (err: any) {
      const code = err?.data?.error?.code || 'Unknown';
      if (scheduleId) {
        window.showNotification({
          titleMsg: t('reportCommon.alert.updateSchedule.fail'),
          bodyMsg: t('reportCommon.alert.updateSchedule.failDesc'),
          severity: 'error',
        });
      } else {
        if (['OutOfRangeInput'].indexOf(code) >= 0) {
          window.showNotification({
            titleMsg: t('reportCommon.alert.createSchedule.fail'),
            bodyMsg: t(`reportCommon.alert.createSchedule.failDesc.${code}`, {
              detail: err?.data?.error?.details[0]?.limit,
            }),
            severity: 'error',
          });
        } else {
          window.showNotification({
            titleMsg: t('reportCommon.alert.createSchedule.fail'),
            bodyMsg: t('reportCommon.alert.createSchedule.failDesc'),
            severity: 'error',
          });
        }
      }
    } finally {
      reportPendo('Report.NewSchedule.Generated.Click', {
        sections: JSON.stringify(params.content),
        frequency,
      });
      setLoading(false);
      setDisabled(false);
    }
  };

  // locating to error position
  const scrollToError = (elementId: string) => {
    const errorBoundingClient =
      document.getElementById(elementId)?.getBoundingClientRect().top || 0;
    const headerBoundingClient =
      document.getElementById('generalSettingsTitle')?.getBoundingClientRect()
        .top || 0;
    (document.querySelector('.app-body') as Element).scrollTop =
      errorBoundingClient - headerBoundingClient - window.innerHeight / 2;
  };

  // fetch and set provisioned services
  const fetchAndSetProvisionedServices = async (orgId: string) => {
    const provisionedServiceTypeList = (
      await ProvisionApi.getProvisionedServicesByOrgId(orgId)
    ).data;
    const provisionedServiceNames = getServiceNamesMapping(
      SERVICE_TYPES,
      provisionedServiceTypeList
    ).filter(
      //report don't need 'microsoft_identity_protection' and 'microsoft_information_protection',
      (s) =>
        ![
          ServiceNameEnum.MicrosoftIdentityProtection,
          ServiceNameEnum.MicrosoftInformationProtection,
        ].includes(s)
    );
    setCheckedServices(provisionedServiceNames);
  };

  useEffect(() => {
    setReportName(
      (loginUserModel.user?.accountName || '') +
        t('reportCommon.placeholders.defaultScheduleName')
    );
    const clpEmail = loginUserModel.user?.accountEmail;
    if (clpEmail) {
      setAccountOpts([
        {
          account: clpEmail,
          email: clpEmail,
        },
      ]);
      setRecipients([clpEmail]);
    }
  }, [loginUserModel.user]);

  useEffect(() => {
    const tagErrors = document
      .querySelector('#recipient-select')
      ?.querySelectorAll('.select-tag-error');
    if (
      (tagErrors && tagErrors.length > 0) ||
      recipients.join('').length > 255
    ) {
      setRecipientError(true);
    } else {
      setRecipientError(false);
    }
  }, [recipients]);

  const nameInput = useMemo(() => {
    return (
      <div>
        <Input
          id="scheduleName"
          type="text"
          placeholder={t('reportCommon.placeholders.name')}
          value={reportName}
          onChange={(e: any) => setReportName(e.target.value)}
          error={reportName === ''}
          maxLength={200}
          width={450}
        />
        {reportName === '' && (
          <Text
            fontSize="sm"
            lineHeight="sm"
            color={colorStyle.color.error}
            textAlign="left"
          >
            {t('reportCommon.tips.errors.requiredField')}
          </Text>
        )}
      </div>
    );
  }, [reportName]);
  const descriptionInput = useMemo(() => {
    return (
      <Textarea
        mt="1x"
        maxLength={2048}
        rows={3}
        placeholder={t('reportCommon.placeholders.description')}
        value={description}
        onChange={(e: any) => setDescription(e.target.value)}
        width={450}
      />
    );
  }, [description]);

  const subjectInput = useMemo(() => {
    return (
      <Textarea
        mt="1x"
        rows={1}
        width={400}
        placeholder={t('reportCommon.placeholders.subject')}
        value={recipientForm.subject}
        onChange={(e: any) =>
          setRecipientForm((preState: { message: string; subject: string }) => {
            return {
              ...preState,
              subject: e.target.value,
            };
          })
        }
      />
    );
  }, [recipientForm.subject]);

  const templateList = useMemo(() => {
    return TEMPLATE_LIST.map((item) => {
      return { key: item.key, value: t(item.value) };
    });
  }, []);

  const templateI18nValue = useMemo(() => {
    const l18nKey = TEMPLATE_LIST.find(
      (item: any) => item.key === template
    )?.value;
    return t(l18nKey as string);
  }, [template, t]);

  const dataPeriodI18nValue = useMemo(() => {
    const I18nKey = SCHEDULE_FREQUENCY_TIP.find((item) => {
      return item.key === frequency;
    })?.value;
    return t(I18nKey as string);
  }, [frequency, t]);

  return (
    <div style={{ position: 'relative' }}>
      <Box {...primaryBoxStyle} textAlign="left" pb="175px">
        <NavBack onBack={goBack} />
        {pageLoading ? (
          <Spinner className="editScheduleSpinner" />
        ) : (
          <>
            <Box px="6x" pt="55px" pb="6x">
              <BlockTitle
                id="generalSettingsTitle"
                content={t('reportCommon.titles.generalSettings')}
              ></BlockTitle>
              <Grid
                templateColumns="130px max-content"
                // columnGap="10x"
                rowGap="18px"
              >
                {/*Status check*/}
                <MemoTextLabel label="reportCommon.labels.status" />
                <Flex align="center">
                  <Switch
                    checked={status}
                    onChange={(e: any) => {
                      setStatus(e.target.checked);
                    }}
                  />
                  <Space width="8px" />
                  <Text>
                    {status
                      ? t('reportCommon.labels.enabled')
                      : t('reportCommon.labels.disabled')}
                  </Text>
                </Flex>
                {/*ReportName*/}
                <MemoTextLabel
                  label="reportCommon.labels.name"
                  required={true}
                />
                {nameInput}
                {/*Frequency*/}
                <MemoTextLabel label="reportCommon.labels.frequency" />
                <Flex align="center">
                  <MenuWithLabel
                    menuItems={SCHEDULE_FREQUENCY.map((item) => {
                      return { key: item.key, value: t(item.value) };
                    })}
                    minWidth={190}
                    selectedKey={frequency}
                    onClick={(e: any) => {
                      setFrequency(e.target.getAttribute('value'));
                    }}
                  />
                  <Text mx="2x" mt="2px" fontSize="14px">
                    {t('reportCommon.placeholders.at')}
                  </Text>
                  <Grid position="relative">
                    <Flex
                      align="center"
                      position="absolute"
                      left={0}
                      top={8}
                      px="3x"
                      // The z-index value should be at least 3 for the prepeneded input adornment
                      zIndex={3}
                    >
                      <Icon as={ClockIcon} color={colorStyle.color.secondary} />
                    </Flex>
                    <Flex align="center">
                      <Input
                        id="frequencyDayTime"
                        width={190}
                        px="10x"
                        value={time}
                        onChange={(e: any) => {
                          setTime(e.target.value);
                          setTimezoneOffset(offset);
                          timeValidate(e.target.value);
                        }}
                        error={timeFormatError}
                      />
                      <TimezoneTip timezoneOffset={timezoneOffset} />
                    </Flex>
                    {timeFormatError && (
                      <Text
                        fontSize="sm"
                        lineHeight="sm"
                        color={colorStyle.color.error}
                        textAlign="left"
                      >
                        {t('reportCommon.tips.errors.timeFormat')}
                      </Text>
                    )}
                  </Grid>
                  {frequency === Frequency.Weekly && (
                    <Flex align="center">
                      <Text mx="2x" fontSize="14px">
                        {t('reportCommon.placeholders.on')}
                      </Text>
                      <MenuWithLabel
                        menuItems={WEEK_DAY.map((item) => {
                          return { key: item.key, value: t(item.value) };
                        })}
                        minWidth={190}
                        selectedKey={dayOfWeek}
                        onClick={(event: any) => {
                          const val = event.target.getAttribute('value');
                          setDayOfWeek(val);
                        }}
                      />
                      <Space width="3x"></Space>
                    </Flex>
                  )}
                  {frequency === Frequency.Monthly && (
                    <Flex align="center">
                      <Text mx="2x" fontSize="14px">
                        {t('reportCommon.placeholders.monthStart')}
                      </Text>
                      <MenuWithLabel
                        menuItems={MONTH_DAY}
                        minWidth={80}
                        selectedKey={dayOfMonth}
                        onClick={(e: any) => {
                          setDayOfMonth(e.target.getAttribute('value'));
                        }}
                      />
                      <Info
                        content={t('reportCommon.tips.hints.monthDay')}
                        iconStyle={{ ml: '2x' }}
                      />
                    </Flex>
                  )}
                </Flex>
                {/*Description*/}
                <MemoTextLabel label="reportCommon.labels.description" />
                {descriptionInput}
                {/* Organization */}
                <MemoTextLabel
                  label="reportCommon.labels.organization"
                  required={true}
                />
                <MenuWithLabel
                  menuItems={orgStore.orgs || []}
                  minWidth={287}
                  selectedKey={orgId}
                  onClick={(e: any) => {
                    const value = e.target.getAttribute('value');
                    setOrgId(value);
                    fetchAndSetProvisionedServices(value);
                  }}
                />

                {/* Services */}
                <MemoTextLabel
                  label="reportCommon.labels.services"
                  required={true}
                />
                <ServiceCheckbox
                  checkedServices={checkedServices}
                  setCheckedServices={setCheckedServices}
                />
              </Grid>
              <BlockTitle
                content={t('reportCommon.titles.reportContent')}
                paddingTop="59px"
              ></BlockTitle>
              <Grid
                templateColumns="130px max-content"
                // columnGap="10x"
                rowGap="18px"
              >
                <MemoTextLabel label="reportCommon.labels.template" />
                {templateList.length === 1 ? (
                  <Text pt="6px">{templateList[0].value}</Text>
                ) : (
                  <MenuWithLabel
                    menuItems={templateList}
                    minWidth={304}
                    selectedKey={template}
                    onClick={(e: any) => {
                      setTemplate(e.target.getAttribute('value'));
                    }}
                  />
                )}

                <MemoTextLabel label="reportCommon.labels.dataPeriod" />
                <Text pt="6px">{dataPeriodI18nValue}</Text>

                <MemoTextLabel label="reportCommon.labels.sections" />
                <ReportContentCheckbox
                  reportContent={reportContent}
                  setReportContent={setReportContent}
                />
              </Grid>
              <BlockTitle
                content={t('reportCommon.titles.format')}
                paddingTop="59px"
              ></BlockTitle>
              <Grid templateColumns="130px max-content" rowGap="3x">
                <MemoTextLabel label="reportCommon.labels.format" />
                <Grid>
                  <Text pt="6px">{t(`reportCommon.labels.${formatType}`)}</Text>
                  <FormatContainer
                    formatContent={formatContent}
                    setFormatContent={setFormatContent}
                    withEditPermission={common.withEditPermission(
                      PermissionNameEnum.report,
                      permissionModel.permissions
                    )}
                  />
                </Grid>
              </Grid>

              <BlockTitle
                content={t('reportCommon.titles.notification')}
                paddingTop="68px"
              ></BlockTitle>
              <Text pt="6px" pb="12px" color={colorStyle.color.primary}>
                {t('reportCommon.titles.notificationSub')}
              </Text>
              <Grid templateColumns="130px max-content" rowGap="18px">
                <MemoTextLabel label="reportCommon.labels.recipients" />
                <Grid id="recipient-select">
                  <SelectTag
                    width="400px"
                    options={accountOpts}
                    fieldNames={{
                      label: 'account',
                      value: 'email',
                    }}
                    onChange={handleEmailChange}
                    rule={validateEmail}
                    selections={recipients}
                    inputError={recipientError}
                  />
                  {recipientError && (
                    <Text
                      fontSize="sm"
                      lineHeight="sm"
                      color={colorStyle.color.error}
                      textAlign="left"
                    >
                      {recipients.join('').length > 255
                        ? t('reportCommon.tips.errors.emailLength')
                        : t('reportCommon.tips.errors.emailFormat')}
                    </Text>
                  )}
                  <Text mt="4px" color={colorStyle.color.secondary}>
                    {t('reportCommon.tips.hints.recipientsInput')}
                  </Text>
                </Grid>

                <MemoTextLabel label="reportCommon.labels.subject" />
                {subjectInput}

                <MemoTextLabelWithInfo
                  label="reportCommon.labels.message"
                  infoLabel="reportCommon.tips.hints.message"
                />
                <Grid rowGap="12px">
                  <Textarea
                    mt="1x"
                    rows={1}
                    placeholder={t('reportCommon.placeholders.optional')}
                    value={recipientForm.message}
                    onChange={(e: any) =>
                      setRecipientForm({
                        ...recipientForm,
                        message: e.target.value,
                      })
                    }
                    maxLength={1024}
                    width={400}
                  />
                  <Button
                    width="max-content"
                    onClick={() => previewOpenToggle(true)}
                  >
                    {t('reportCommon.buttons.preview')}
                  </Button>
                </Grid>
              </Grid>
            </Box>
            {/* Preview Modal */}
            <ReportPreviewModal
              isPreviewOpen={isPreviewOpen}
              onClose={previewOpenToggle}
              data={{
                name: reportName || '-',
                template: templateI18nValue || '-',
                sections: Object.keys(reportContent)
                  .filter((value, index) => {
                    return Object.values(reportContent)[index] === true;
                  })
                  .map((item) => {
                    return t(`reportCommon.content.${item}`);
                  })
                  .join(i18n.language === 'ja' ? '、' : ', '),
                reportType: t('reportCommon.preview.scheduledReport'),
                message: recipientForm.message,
              }}
            />
            {/* Bottom button */}
            <Flex
              position="fixed"
              pl="5x"
              bottom={0}
              left={siderWidth}
              right={0}
              background={colorStyle.background.secondary}
              height={64}
              align="center"
              justify="flex-start"
            >
              <LoadingBtn
                loading={loading}
                disabled={
                  disabled ||
                  !common.withEditPermission(
                    PermissionNameEnum.report,
                    permissionModel.permissions
                  )
                }
                onClick={async () => {
                  await submitData();
                }}
                variant="primary"
              >
                {loading
                  ? scheduleId
                    ? t('reportCommon.buttons.saving')
                    : t('reportCommon.buttons.creating')
                  : scheduleId
                    ? t('reportCommon.buttons.save')
                    : t('reportCommon.buttons.create')}
              </LoadingBtn>
              <Space width="2x"></Space>
              <Button onClick={resetReportData}>
                {t('reportCommon.buttons.reset')}
              </Button>
              <Space width="2x"></Space>
              <ViewPDFSampleButton templateId={template} format={formatType} />
            </Flex>
          </>
        )}
      </Box>
    </div>
  );
}
